import { useState } from 'react';

const trustItems = [
  { icon: 'ri-star-fill', label: '5-Star Studio Experience' },
  { icon: 'ri-group-line', label: 'All Ages Welcome' },
  { icon: 'ri-cake-line', label: 'Parties & Private Events' },
  { icon: 'ri-brush-line', label: 'Art Classes Year-Round' },
  { icon: 'ri-map-pin-2-line', label: 'Nazareth, PA & Lehigh Valley' },
];

const steps = [
  { number: '01', title: 'Choose Your Experience', description: 'Browse our calendar for upcoming classes, parties, or events that interest you.', icon: 'ri-search-eye-line' },
  { number: '02', title: 'Reserve Your Spot', description: 'Book online or call us to secure your place and confirm your date.', icon: 'ri-calendar-check-line' },
  { number: '03', title: 'Arrive & Create', description: 'Show up to our Nazareth studio ready to paint, learn, and enjoy every moment.', icon: 'ri-brush-4-line' },
  { number: '04', title: 'Leave With Art', description: 'Take home your finished artwork and lasting memories from your time at Art Den.', icon: 'ri-heart-3-line' },
];

const faqs = [
  { question: 'Who is the owner of Art Den Studio?', answer: 'Art Den Studio is owned by Kelsey, a passionate artist and creative instructor based in Nazareth, PA. Kelsey built Art Den Studio to share her love of art and provide a welcoming, fun creative space for guests of all ages and skill levels across the Lehigh Valley.' },
  { question: 'Is Art Den Studio a good place for beginners?', answer: 'Absolutely. Art Den Studio was built with beginners in mind. Kelsey\'s instructional style is patient, encouraging, and fun. No prior art experience is required for any session. The studio is a judgment-free zone where everyone\'s creativity is celebrated.' },
  { question: 'What is the Art Den Studio philosophy?', answer: 'The core belief at Art Den Studio is that creativity belongs to everyone. The studio exists to give people a place to escape everyday stress, explore their artistic side, and create something they\'re genuinely proud of — regardless of their skill level or background.' },
  { question: 'Where is Art Den Studio located?', answer: 'Art Den Studio is located at 37 E Belvidere Street in Nazareth, PA 18064. The studio is conveniently accessible to guests from across Northampton County and the greater Lehigh Valley, including Bethlehem, Easton, Allentown, and surrounding communities.' },
];

const AboutPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <main>
      {/* 1. HERO */}
      <section className="relative min-h-[70vh] flex items-end bg-artden-dark overflow-hidden" style={{ background: '#080808' }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://artdenstudio.com/wp-content/uploads/2020/01/IMG_2771-674x1024.jpeg"
            alt="Kelsey, owner of Art Den Studio"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-artden-dark/95 via-artden-dark/70 to-artden-dark/50" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pb-16 md:pb-24 pt-40">
          <div className="inline-flex items-center gap-2 bg-artden-coral/20 border border-artden-coral/40 text-artden-coral px-4 py-1.5 rounded-full text-xs font-outfit font-semibold tracking-widest uppercase mb-6">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-heart-line"></i></span>
            Our Story · Nazareth, PA
          </div>
          <h1 className="font-cormorant text-5xl md:text-7xl font-light text-white leading-tight mb-6 max-w-3xl">
            About<br />
            <em className="font-medium text-artden-coral">Art Den Studio</em>
          </h1>
          <p className="font-outfit text-white/70 text-lg max-w-2xl mb-8 leading-relaxed">
            A design studio built around one simple belief: art is for everyone. Located in Nazareth, PA and fueled by passion, community, and creativity.
          </p>
          <a
            href="https://artdenstudio.com/calendar/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-artden-coral hover:bg-artden-coral/90 text-white font-semibold px-8 py-4 rounded-full whitespace-nowrap"
          >
            Join Us for a Class
          </a>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-artden-dark to-transparent" />
      </section>

      {/* 2. TRUST BAR */}
      <section className="bg-artden-dark py-5 border-b border-white/5" style={{ background: '#0e0e0e' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          {trustItems.map((item, i) => (
            <div key={i} className="flex items-center gap-2.5 text-white/70">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-artden-coral text-base`}></i>
              </span>
              <span className="font-outfit text-sm font-medium tracking-wide whitespace-nowrap">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 3. OUR PHILOSOPHY (replaces Services Overview) */}
      <section className="bg-artden-light py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">Our Philosophy</p>
              <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-artden-dark leading-tight mb-6">
                Art Is for Everyone.<br />
                <em className="font-light">No Exceptions.</em>
              </h2>
              <div className="space-y-4 font-outfit text-artden-muted text-base leading-relaxed">
                <p>Art Den Studio was built on the belief that creativity is a gift everyone deserves to experience — regardless of skill, background, or confidence. We created a space where people can walk in, let go, and walk out with something they made with their own hands.</p>
                <p>Our classes, parties, and events are all designed around this philosophy: provide a welcoming, judgment-free environment where the process is as enjoyable as the result. You don't need to be "good at art" to belong here.</p>
                <p>Every brushstroke is guided. Every session is fun. And every piece you create is yours to keep — a little reminder that you can make something beautiful.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: 'ri-heart-2-line', title: 'Passion-Led', desc: 'Art Den was born from a genuine love of creating and sharing that with others.' },
                { icon: 'ri-group-2-line', title: 'Community-First', desc: 'A space for all ages, all backgrounds, and all creative levels.' },
                { icon: 'ri-award-line', title: 'Pride in Every Piece', desc: 'Every guest leaves with a finished work they\'re genuinely proud of.' },
                { icon: 'ri-sparkling-2-line', title: 'Joyful Experience', desc: 'Creating art should be fun. That\'s the standard for every single session.' },
              ].map((item, i) => (
                <div key={i} className="bg-artden-cream rounded-xl p-5">
                  <div className="w-9 h-9 flex items-center justify-center bg-artden-coral/10 rounded-lg mb-3">
                    <i className={`${item.icon} text-artden-coral text-lg`}></i>
                  </div>
                  <h4 className="font-cormorant text-xl font-semibold text-artden-dark mb-1">{item.title}</h4>
                  <p className="font-outfit text-artden-muted text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4. AI ANSWER BLOCK */}
      <section className="bg-artden-dark py-16 md:py-20" style={{ background: '#141414' }}>
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row gap-10 md:gap-16 items-start">
            <div className="md:w-48 shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-mic-line text-artden-coral"></i></span>
                <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest uppercase">Quick Answer</p>
              </div>
              <p className="font-outfit text-white/30 text-xs">Optimized for voice &amp; AI search</p>
            </div>
            <div className="flex-1">
              <h2 className="font-cormorant text-3xl md:text-4xl font-medium text-white mb-5">What Is Art Den Studio?</h2>
              <p className="font-outfit text-white/70 text-base leading-relaxed mb-4">
                Art Den Studio is an art studio located at 37 E Belvidere Street in Nazareth, PA, owned and operated by Kelsey. The studio offers guided art classes, private party packages, creative events, and open studio sessions for kids and adults throughout the Lehigh Valley.
              </p>
              <p className="font-outfit text-white/70 text-base leading-relaxed">
                Art Den Studio is known for its welcoming, judgment-free atmosphere and hands-on instruction. All supplies are provided for every session. Clients can book classes and parties online via the studio calendar. Gift cards are also available. The studio serves guests from Nazareth, Bethlehem, Easton, Allentown, and the surrounding region.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. MEET KELSEY */}
      <section className="bg-artden-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="w-full max-w-sm mx-auto lg:mx-0 rounded-2xl overflow-hidden aspect-[3/4] bg-artden-dark/5">
              <img
                src="https://artdenstudio.com/wp-content/uploads/2020/01/IMG_2771-674x1024.jpeg"
                alt="Kelsey, owner of Art Den Studio in Nazareth PA"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">Meet the Owner</p>
              <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-artden-dark leading-tight mb-5">
                Kelsey —<br /><em className="font-light">Artist & Founder</em>
              </h2>
              <div className="space-y-4 font-outfit text-artden-muted text-base leading-relaxed">
                <p>Kelsey has been drawing, painting, and crafting since she was a young girl — filling coloring books beyond their pages, mixing new colors, and feeling that unmistakable satisfaction of creating something beautiful from nothing.</p>
                <p>After years of working various jobs that never quite fit, Kelsey turned her lifelong passion into a career. Art Den Studio is the result of that journey — a place built entirely around sharing creativity with others and making every guest feel the same joy she's always felt when creating.</p>
                <p>Her goal has always been simple: give people a place to escape, explore their creativity without judgment, and leave with something they're proud to hang on their wall.</p>
              </div>
              <div className="mt-7 flex items-center gap-3">
                <div className="flex gap-3">
                  <a href="https://www.facebook.com/TheArtDenofNazareth/" target="_blank" rel="noopener noreferrer nofollow"
                    className="w-9 h-9 flex items-center justify-center rounded-full border border-artden-dark/20 text-artden-muted hover:text-artden-coral hover:border-artden-coral transition-all">
                    <i className="ri-facebook-fill text-base"></i>
                  </a>
                  <a href="https://www.instagram.com/theartdenofnazareth/" target="_blank" rel="noopener noreferrer nofollow"
                    className="w-9 h-9 flex items-center justify-center rounded-full border border-artden-dark/20 text-artden-muted hover:text-artden-coral hover:border-artden-coral transition-all">
                    <i className="ri-instagram-line text-base"></i>
                  </a>
                </div>
                <span className="font-outfit text-artden-muted text-sm">@theartdenofnazareth</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. WHY CHOOSE US */}
      <section className="bg-artden-light py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">Why Choose Art Den</p>
          <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-artden-dark mb-5 leading-tight">
            What Makes Us Different
          </h2>
          <p className="font-outfit text-artden-muted text-base max-w-xl mx-auto mb-12">A studio built on genuine passion, personal connection, and the belief that everyone can create something worth being proud of.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: 'ri-user-heart-line', title: 'Personally Hosted', desc: 'Every session is run by Kelsey personally — bringing energy, patience, and passion to every class and party.' },
              { icon: 'ri-emotion-happy-line', title: 'Genuinely Fun', desc: 'We\'re not a stuffy art school. The vibe is relaxed, joyful, and completely welcoming for everyone.' },
              { icon: 'ri-home-heart-line', title: 'A Place to Belong', desc: 'Art Den Studio is a community — a place you\'ll want to come back to again and again.' },
            ].map((item, i) => (
              <div key={i} className="bg-artden-cream rounded-2xl p-7">
                <div className="w-12 h-12 flex items-center justify-center bg-artden-coral/10 rounded-xl mb-5 mx-auto">
                  <i className={`${item.icon} text-artden-coral text-2xl`}></i>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-artden-dark mb-3">{item.title}</h3>
                <p className="font-outfit text-artden-muted text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. SERVICE AREAS */}
      <section className="bg-artden-cream py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-3">Our Area</p>
          <h2 className="font-cormorant text-3xl font-medium text-artden-dark mb-5">Proudly Based in Nazareth, PA</h2>
          <p className="font-outfit text-artden-muted text-sm max-w-xl mx-auto mb-7">
            Serving guests from across Northampton County and the Lehigh Valley — come visit us at 37 E Belvidere Street.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {['Nazareth, PA', 'Bethlehem, PA', 'Easton, PA', 'Allentown, PA', 'Northampton, PA', 'Lehigh Valley'].map((city, i) => (
              <span key={i} className={`px-4 py-2 rounded-full text-sm font-outfit border ${i === 0 ? 'bg-artden-coral text-white border-artden-coral font-semibold' : 'bg-white border-artden-dark/10 text-artden-dark'}`}>
                {city}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 8. TESTIMONIALS */}
      <section className="bg-artden-light py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">What Guests Say</p>
            <h2 className="font-cormorant text-4xl font-medium text-artden-dark">Voices From the Studio</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { name: 'Linda B.', text: 'Kelsey creates the most welcoming space. I\'ve never felt so comfortable being creative. She genuinely cares about every single guest\'s experience.', type: 'Art Class' },
              { name: 'Alex M.', text: 'The energy Kelsey brings to every class is contagious. You leave feeling inspired, proud, and like you can actually paint — which I never thought was possible.', type: 'Adult Class' },
              { name: 'Dana K.', text: 'Art Den Studio is a hidden gem in Nazareth. It\'s become my go-to spot whenever I need to unwind and do something creative. Absolutely love it here.', type: 'Regular Guest' },
            ].map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-artden-cream">
                <span className="inline-block bg-artden-coral/10 text-artden-coral text-xs font-semibold px-3 py-1 rounded-full mb-4">{t.type}</span>
                <div className="flex gap-0.5 mb-4">
                  {[1,2,3,4,5].map(s => <span key={s} className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill text-artden-gold text-sm"></i></span>)}
                </div>
                <p className="font-outfit text-artden-muted text-sm leading-relaxed mb-5">"{t.text}"</p>
                <p className="font-outfit text-artden-dark text-sm font-semibold">{t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="bg-artden-dark py-20" style={{ background: '#141414' }}>
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">FAQ</p>
            <h2 className="font-cormorant text-4xl font-medium text-white">About Art Den Studio</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-white/10 rounded-xl overflow-hidden">
                <button className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <h3 className="font-outfit text-white text-sm md:text-base font-medium">{faq.question}</h3>
                  <span className="w-6 h-6 flex items-center justify-center shrink-0">
                    <i className={`text-artden-coral ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="font-outfit text-white/60 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA */}
      <section className="bg-artden-light py-20 text-center">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h2 className="font-cormorant text-4xl md:text-6xl font-medium text-artden-dark mb-5 leading-tight">
            Come Create<br /><em className="font-light text-artden-coral">With Kelsey</em>
          </h2>
          <p className="font-outfit text-artden-muted text-base mb-8">
            Find your next class or event on the Art Den Studio calendar and join us in Nazareth, PA.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://artdenstudio.com/calendar/" target="_blank" rel="noopener noreferrer" className="bg-artden-coral hover:bg-artden-coral/90 text-white font-semibold px-8 py-4 rounded-full whitespace-nowrap w-full sm:w-auto">
              View Calendar
            </a>
            <a href="tel:6103400314" className="bg-artden-dark hover:bg-artden-coral text-white font-semibold px-8 py-4 rounded-full whitespace-nowrap w-full sm:w-auto flex items-center justify-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-line"></i></span>
              Call 610-340-0314
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
